import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getUnits, getWorkshopContents, createWorkshopContent, createWorkshopBlock } from '@/lib/db'
import type { Unit, WorkshopContent } from '@/types'
import BlockConfigForm from './BlockConfigForms'
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Wand2,
  FileText,
  Layers,
  Save,
  Download,
  Eye,
  Plus,
  Trash2,
  AlertCircle
} from 'lucide-react'

// ============================================
// Types
// ============================================
type WizardMode = 'select' | 'structured' | 'free'

interface BlockDraft {
  id: string
  section: string
  block_type: string
  title: string
  instructions: string
  teacher_notes: string
  config: Record<string, any>
  estimated_minutes: number
}

interface SectionDraft {
  name: string
  blocks: BlockDraft[]
}

// ============================================
// Structured template based on tallerU1.sql
// ============================================
const BLOCK_TYPES = [
  { value: 'title_slide', label: 'Título / Portada' },
  { value: 'vocabulary', label: 'Vocabulario' },
  { value: 'phrase_list', label: 'Lista de frases' },
  { value: 'dialogue', label: 'Diálogo' },
  { value: 'roulette', label: 'Ruleta' },
  { value: 'flashcards', label: 'Flashcards' },
  { value: 'matching', label: 'Matching / Unir' },
  { value: 'fill_blanks', label: 'Completar huecos' },
  { value: 'role_play', label: 'Role Play' },
  { value: 'image_describe', label: 'Describir imagen' },
  { value: 'poll', label: 'Encuesta / Quiz' },
  { value: 'timer_activity', label: 'Actividad cronometrada' },
  { value: 'free_html', label: 'HTML libre' },
  { value: 'video_embed', label: 'Video' },
  { value: 'audio_listen', label: 'Audio' },
  { value: 'sentence_builder', label: 'Ordenar oraciones' },
  { value: 'conversation_prompts', label: 'Conversación guiada' },
]

const STRUCTURED_TEMPLATE: { name: string; blocks: Omit<BlockDraft, 'id'>[] }[] = [
  {
    name: 'Calentamiento',
    blocks: [
      { section: 'Calentamiento', block_type: 'title_slide', title: 'Bienvenida', instructions: 'Proyectar mientras los alumnos llegan. Dar la bienvenida en francés.', teacher_notes: '', config: { heading: '', subheading: '', background_color: '#1e3a5f', show_date: true }, estimated_minutes: 1 },
      { section: 'Calentamiento', block_type: 'poll', title: 'Rompehielo', instructions: 'Preguntar a mano alzada. Sirve para calibrar el grupo.', teacher_notes: '', config: { question: '', options: [{ text: '', is_correct: false }], show_correct: false, explanation: '' }, estimated_minutes: 2 },
      { section: 'Calentamiento', block_type: 'vocabulary', title: 'Primeras palabras', instructions: 'Leer cada palabra en voz alta. Pedir que repitan en coro.', teacher_notes: '', config: { title: '', words: [{ fr: '', es: '', phonetic: '', example: '' }], show_phonetic: true, columns: 1 }, estimated_minutes: 2 },
    ]
  },
  {
    name: 'Sección temática 1',
    blocks: [
      { section: '', block_type: 'vocabulary', title: 'Vocabulario del tema', instructions: 'Leer cada palabra. Pedir repetición en coro.', teacher_notes: '', config: { title: '', words: [{ fr: '', es: '', phonetic: '', example: '' }], show_phonetic: true, columns: 2 }, estimated_minutes: 2 },
      { section: '', block_type: 'phrase_list', title: 'Frases clave', instructions: 'Leer cada frase. Practicar con los alumnos.', teacher_notes: '', config: { categories: [{ name: '', phrases: [{ fr: '', es: '', phonetic: '', context: '' }] }], highlight_key_words: true }, estimated_minutes: 2 },
      { section: '', block_type: 'dialogue', title: 'Mini diálogo', instructions: 'Leer con dos voces. Luego pedir que repitan en parejas.', teacher_notes: '', config: { title: '', characters: [{ name: '', color: '#3b82f6' }, { name: '', color: '#ef4444' }], lines: [{ character: '', fr: '', es: '' }], show_translation: true }, estimated_minutes: 2 },
      { section: '', block_type: 'flashcards', title: 'Flashcards', instructions: 'Mostrar tarjeta, que digan el significado ANTES de voltear.', teacher_notes: '', config: { cards: [{ front: '', back: '', hint: '' }], shuffle: true, show_hint: true, layout: 'grid' }, estimated_minutes: 2 },
      { section: '', block_type: 'matching', title: 'Unir pares', instructions: 'Actividad grupal: pedir voluntarios que unan los pares.', teacher_notes: '', config: { pairs: [{ left: '', right: '' }], left_label: 'Francais', right_label: 'Espanol', shuffle_sides: true }, estimated_minutes: 2 },
      { section: '', block_type: 'roulette', title: 'Ruleta', instructions: 'Girar la ruleta. El alumno debe responder en francés.', teacher_notes: '', config: { items: [''], colors: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b'], title: '', subtitle: '', spin_duration_ms: 3500 }, estimated_minutes: 2 },
      { section: '', block_type: 'timer_activity', title: 'Práctica en parejas', instructions: 'Formar parejas. Practicar durante el tiempo indicado.', teacher_notes: '', config: { activity: '', activity_en: '', duration_seconds: 120, tips: [''], sound_on_finish: true }, estimated_minutes: 2 },
    ]
  },
  {
    name: 'Sección temática 2',
    blocks: [
      { section: '', block_type: 'title_slide', title: 'Título de sección', instructions: 'Transición al nuevo tema.', teacher_notes: '', config: { heading: '', subheading: '', background_color: '#065f46' }, estimated_minutes: 1 },
      { section: '', block_type: 'vocabulary', title: 'Vocabulario', instructions: 'Leer y pedir repetición.', teacher_notes: '', config: { title: '', words: [{ fr: '', es: '', phonetic: '', example: '' }], show_phonetic: true, columns: 1 }, estimated_minutes: 2 },
      { section: '', block_type: 'phrase_list', title: 'Frases', instructions: 'Practicar pregunta-respuesta.', teacher_notes: '', config: { categories: [{ name: '', phrases: [{ fr: '', es: '', phonetic: '', context: '' }] }], highlight_key_words: true }, estimated_minutes: 2 },
      { section: '', block_type: 'flashcards', title: 'Flashcards', instructions: 'Mostrar y que adivinen.', teacher_notes: '', config: { cards: [{ front: '', back: '', hint: '' }], shuffle: false, show_hint: true, layout: 'grid' }, estimated_minutes: 2 },
      { section: '', block_type: 'dialogue', title: 'Diálogo', instructions: 'Leer diálogo. Pedir voluntarios.', teacher_notes: '', config: { title: '', characters: [{ name: '', color: '#8b5cf6' }, { name: '', color: '#f59e0b' }], lines: [{ character: '', fr: '', es: '' }], show_translation: true }, estimated_minutes: 2 },
      { section: '', block_type: 'fill_blanks', title: 'Completar frases', instructions: 'Leer con hueco. Que completen.', teacher_notes: '', config: { sentences: [{ text: '', answer: '', hint: '' }], show_hints: true, reveal_mode: 'one_by_one' }, estimated_minutes: 2 },
      { section: '', block_type: 'sentence_builder', title: 'Ordenar palabras', instructions: 'Palabras desordenadas, que ordenen.', teacher_notes: '', config: { sentences: [{ words: [], correct_order: [], translation: '' }], show_translation: true }, estimated_minutes: 2 },
      { section: '', block_type: 'timer_activity', title: 'Práctica', instructions: 'Practicar en parejas.', teacher_notes: '', config: { activity: '', activity_en: '', duration_seconds: 180, tips: [''], sound_on_finish: true }, estimated_minutes: 3 },
    ]
  },
  {
    name: 'Practica integrada',
    blocks: [
      { section: 'Practica integrada', block_type: 'title_slide', title: 'A practicar!', instructions: '"Ahora vamos a usar TODO lo que aprendimos."', teacher_notes: '', config: { heading: 'Pratique !', subheading: 'Vamos a usar todo lo aprendido', background_color: '#581c87' }, estimated_minutes: 1 },
      { section: 'Practica integrada', block_type: 'conversation_prompts', title: 'Conversación guiada', instructions: 'Formar parejas. Mostrar cada pregunta y dar tiempo.', teacher_notes: '', config: { prompts: [{ question_en: '', question_es: '', model_answer: '', difficulty: 'easy' }], mode: 'sequential', pairs_or_group: 'pairs', time_per_prompt_seconds: 60 }, estimated_minutes: 3 },
      { section: 'Practica integrada', block_type: 'role_play', title: 'Role Play', instructions: 'Actuar la escena usando todo lo aprendido.', teacher_notes: '', config: { scenario: '', scenario_en: '', roles: [{ name: '', description: '', suggested_phrases: [] }], duration_minutes: 3, tips: '' }, estimated_minutes: 3 },
      { section: 'Practica integrada', block_type: 'roulette', title: 'Ruleta final', instructions: 'Repaso divertido. Celebrar cada acierto.', teacher_notes: '', config: { items: [''], colors: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'], title: '', subtitle: '', spin_duration_ms: 4000 }, estimated_minutes: 3 },
      { section: 'Practica integrada', block_type: 'video_embed', title: 'Video de refuerzo', instructions: 'Poner el video. Pedir que identifiquen lo aprendido.', teacher_notes: '', config: { url: '', title: '', start_seconds: 0, end_seconds: 0, discussion_questions: [''] }, estimated_minutes: 2 },
    ]
  },
  {
    name: 'Cierre',
    blocks: [
      { section: 'Cierre', block_type: 'free_html', title: 'Resumen y despedida', instructions: 'Repasar lo aprendido. Felicitar al grupo. Despedirse EN FRANCÉS.', teacher_notes: '', config: { html: '<div style="text-align:center; padding:20px;"><h1>Bravo !</h1><h2>Hoy aprendimos...</h2><p>Completar con el resumen del taller</p><p style="margin-top:24px; font-size:20px; font-weight:bold;">Au revoir et bonne journee !</p></div>' }, estimated_minutes: 2 },
    ]
  },
]

function uid() {
  return Math.random().toString(36).slice(2, 10)
}

function buildStructuredSections(): SectionDraft[] {
  return STRUCTURED_TEMPLATE.map(t => ({
    name: t.name,
    blocks: t.blocks.map(b => ({
      ...b,
      id: uid(),
      section: b.section || t.name,
    }))
  }))
}

function buildFreeSections(): SectionDraft[] {
  return [{
    name: 'General',
    blocks: [{
      id: uid(),
      section: 'General',
      block_type: 'title_slide',
      title: 'Bienvenida',
      instructions: '',
      teacher_notes: '',
      config: { heading: '', subheading: '', background_color: '#1e3a5f', show_date: true },
      estimated_minutes: 1
    }]
  }]
}

// ============================================
// Main Wizard Component
// ============================================
export default function WorkshopWizard() {
  const navigate = useNavigate()

  // Data
  const [units, setUnits] = useState<Unit[]>([])
  const [existingContents, setExistingContents] = useState<WorkshopContent[]>([])
  const [loading, setLoading] = useState(true)

  // Wizard state
  const [mode, setMode] = useState<WizardMode>('select')
  const [step, setStep] = useState(0) // 0=info, 1..N=sections, N+1=review
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Content data
  const [unitId, setUnitId] = useState('')
  const [title, setTitle] = useState('')
  const [titleFr, setTitleFr] = useState('')
  const [description, setDescription] = useState('')
  const [sections, setSections] = useState<SectionDraft[]>([])

  // Expanded block in current step
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      const [unitsRes, contentsRes] = await Promise.all([getUnits(), getWorkshopContents()])
      if (unitsRes.data) setUnits(unitsRes.data)
      if (contentsRes.data) setExistingContents(contentsRes.data)
      setLoading(false)
    }
    load()
  }, [])

  const usedUnitIds = new Set(existingContents.map(c => c.unit_id))
  const availableUnits = units.filter(u => !usedUnitIds.has(u.id))

  // Steps: 0=info general, 1..N=section steps, N+1=review
  const totalSteps = sections.length + 2
  const isReviewStep = step === totalSteps - 1
  const currentSectionIndex = step - 1

  const allBlocks = sections.flatMap(s => s.blocks)
  const totalMinutes = allBlocks.reduce((sum, b) => sum + (b.estimated_minutes || 0), 0)

  // ---- Mode selection ----
  function selectMode(m: 'structured' | 'free') {
    setMode(m)
    setSections(m === 'structured' ? buildStructuredSections() : buildFreeSections())
    setStep(0)
  }

  // ---- Section manipulation (free mode) ----
  function addSection() {
    setSections(prev => [...prev, { name: 'Nueva seccion', blocks: [] }])
  }

  function removeSection(si: number) {
    setSections(prev => prev.filter((_, i) => i !== si))
    if (step > sections.length) setStep(prev => prev - 1)
  }

  function renameSectionName(si: number, name: string) {
    setSections(prev => prev.map((s, i) => {
      if (i !== si) return s
      return { ...s, name, blocks: s.blocks.map(b => ({ ...b, section: name })) }
    }))
  }

  // ---- Block manipulation ----
  function addBlockToSection(si: number, blockType: string) {
    const section = sections[si]
    const newBlock: BlockDraft = {
      id: uid(),
      section: section.name,
      block_type: blockType,
      title: '',
      instructions: '',
      teacher_notes: '',
      config: getDefaultConfig(blockType),
      estimated_minutes: 2,
    }
    setSections(prev => prev.map((s, i) => i === si ? { ...s, blocks: [...s.blocks, newBlock] } : s))
    setExpandedBlockId(newBlock.id)
  }

  function updateBlock(si: number, bi: number, partial: Partial<BlockDraft>) {
    setSections(prev => prev.map((s, i) => {
      if (i !== si) return s
      return { ...s, blocks: s.blocks.map((b, j) => j === bi ? { ...b, ...partial } : b) }
    }))
  }

  function removeBlock(si: number, bi: number) {
    setSections(prev => prev.map((s, i) => {
      if (i !== si) return s
      return { ...s, blocks: s.blocks.filter((_, j) => j !== bi) }
    }))
  }

  function moveBlock(si: number, bi: number, dir: -1 | 1) {
    setSections(prev => prev.map((s, i) => {
      if (i !== si) return s
      const blocks = [...s.blocks]
      const newIdx = bi + dir
      if (newIdx < 0 || newIdx >= blocks.length) return s
      ;[blocks[bi], blocks[newIdx]] = [blocks[newIdx], blocks[bi]]
      return { ...s, blocks }
    }))
  }

  // ---- Save to DB ----
  async function handleSave() {
    if (!unitId) { setSaveError('Selecciona una unidad'); return }
    if (!title.trim()) { setSaveError('Ingresa un título'); return }
    if (allBlocks.length === 0) { setSaveError('Agrega al menos un bloque'); return }

    setSaving(true)
    setSaveError('')

    const { data: content, error: contentError } = await createWorkshopContent({
      unit_id: unitId,
      title: title.trim(),
      title_en: titleFr.trim() || undefined,
      description: description.trim() || undefined,
      estimated_duration_minutes: totalMinutes,
      is_published: false,
    })

    if (contentError || !content) {
      const msg = (contentError as any)?.message || 'Error al crear el contenido'
      if (msg.includes('unique') || msg.includes('duplicate')) {
        setSaveError('Ya existe material para esa unidad.')
      } else {
        setSaveError(msg)
      }
      setSaving(false)
      return
    }

    // Create blocks sequentially to maintain order
    let sortOrder = 1
    for (const block of allBlocks) {
      const { error: blockError } = await createWorkshopBlock({
        workshop_content_id: content.id,
        sort_order: sortOrder++,
        section: block.section,
        block_type: block.block_type,
        title: block.title || undefined,
        instructions: block.instructions || undefined,
        teacher_notes: block.teacher_notes || undefined,
        config: block.config,
        estimated_minutes: block.estimated_minutes,
      })
      if (blockError) {
        setSaveError(`Error en bloque ${sortOrder - 1}: ${(blockError as any)?.message}`)
        setSaving(false)
        return
      }
    }

    setSaving(false)
    navigate(`/admin/workshop-content/${content.id}/edit`)
  }

  // ---- Export SQL ----
  function exportSQL() {
    const unitLabel = units.find(u => u.id === unitId)
    const unitName = (unitLabel as any)?.name_es || 'Sin unidad'

    let sql = `-- ============================================================\n`
    sql += `-- TALLER: ${title}\n`
    sql += `-- Unidad: ${unitName}\n`
    sql += `-- ${allBlocks.length} bloques / ~${totalMinutes} minutos\n`
    sql += `-- ============================================================\n\n`
    sql += `DO $$\nDECLARE\n  wc_id uuid;\nBEGIN\n\n`
    sql += `INSERT INTO workshop_contents (id, unit_id, title, title_en, description, estimated_duration_minutes, is_published)\nVALUES (\n  gen_random_uuid(),\n  '${unitId}',\n  '${esc(title)}',\n  '${esc(titleFr)}',\n  '${esc(description)}',\n  ${totalMinutes},\n  false\n)\nRETURNING id INTO wc_id;\n\n`

    let sortOrder = 1
    for (const section of sections) {
      sql += `-- ============================================================\n`
      sql += `-- SECCION: ${section.name}\n`
      sql += `-- ============================================================\n\n`
      for (const block of section.blocks) {
        sql += `INSERT INTO workshop_blocks (workshop_content_id, sort_order, section, block_type, title, instructions, teacher_notes, config, estimated_minutes)\n`
        sql += `VALUES (wc_id, ${sortOrder++}, '${esc(block.section)}', '${esc(block.block_type)}', '${esc(block.title)}', '${esc(block.instructions)}', ${block.teacher_notes ? `'${esc(block.teacher_notes)}'` : 'NULL'}, '${esc(JSON.stringify(block.config))}', ${block.estimated_minutes});\n\n`
      }
    }

    sql += `END $$;\n`

    const blob = new Blob([sql], { type: 'text/sql' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `taller_${unitId.slice(0, 8)}.sql`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    )
  }

  // ---- MODE SELECTION ----
  if (mode === 'select') {
    return (
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <button onClick={() => navigate('/admin/workshop-content')} className="text-sm text-gray-500 hover:text-gray-900 mb-4 flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Volver a talleres
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wand2 className="w-7 h-7 text-purple-600" />
            Crear nuevo taller
          </h1>
          <p className="text-gray-500 mt-1">Elige cómo quieres crear tu taller interactivo</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Structured */}
          <button
            onClick={() => selectMode('structured')}
            className="bg-white border-2 border-gray-200 hover:border-purple-500 rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:shadow-purple-200 group"
          >
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
              <Layers className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Estructura guiada</h3>
            <p className="text-sm text-gray-500 mb-4">
              Plantilla predefinida con las secciones y tipos de bloque recomendados.
              Igual que los talleres existentes (Calentamiento, Temas, Práctica, Cierre).
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>~ 5 secciones, ~24 bloques</p>
              <p>Solo llenas el contenido</p>
            </div>
          </button>

          {/* Free */}
          <button
            onClick={() => selectMode('free')}
            className="bg-white border-2 border-gray-200 hover:border-[#0055A4] rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:shadow-[#0055A4]/10 group"
          >
            <div className="w-14 h-14 bg-[#0055A4]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0055A4]/20 transition-colors">
              <FileText className="w-7 h-7 text-[#0055A4]" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Taller libre</h3>
            <p className="text-sm text-gray-500 mb-4">
              Crea secciones y bloques a tu gusto. Total libertad para diseñar
              el taller como quieras.
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>Tu decides la estructura</p>
              <p>Agrega cualquier tipo de bloque</p>
            </div>
          </button>
        </div>

        {availableUnits.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-yellow-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Todas las unidades ya tienen material de taller asignado.
          </div>
        )}
      </div>
    )
  }

  // ---- WIZARD STEPS ----
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header with progress */}
      <div>
        <button onClick={() => { if (step === 0) setMode('select'); else setStep(s => s - 1) }} className="text-sm text-gray-500 hover:text-gray-900 mb-3 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> {step === 0 ? 'Cambiar modo' : 'Paso anterior'}
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            {mode === 'structured' ? <Layers className="w-6 h-6 text-purple-600" /> : <FileText className="w-6 h-6 text-[#0055A4]" />}
            {mode === 'structured' ? 'Estructura guiada' : 'Taller libre'}
          </h1>
          <span className="text-sm text-gray-500">
            Paso {step + 1} de {totalSteps} | {allBlocks.length} bloques | ~{totalMinutes} min
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-2 bg-gray-100 rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300 rounded-full"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
        </div>
        {/* Step labels */}
        <div className="flex mt-2 text-xs text-gray-500 overflow-x-auto gap-1">
          <button onClick={() => setStep(0)} className={`px-2 py-0.5 rounded whitespace-nowrap ${step === 0 ? 'bg-purple-100 text-purple-700' : 'hover:text-gray-600'}`}>
            Info general
          </button>
          {sections.map((s, i) => (
            <button key={i} onClick={() => setStep(i + 1)} className={`px-2 py-0.5 rounded whitespace-nowrap ${step === i + 1 ? 'bg-purple-100 text-purple-700' : 'hover:text-gray-600'}`}>
              {s.name}
            </button>
          ))}
          <button onClick={() => setStep(totalSteps - 1)} className={`px-2 py-0.5 rounded whitespace-nowrap ${isReviewStep ? 'bg-green-100 text-green-700' : 'hover:text-gray-600'}`}>
            Revisar y guardar
          </button>
        </div>
      </div>

      {/* Step content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {step === 0 && (
          <StepInfoGeneral
            unitId={unitId} setUnitId={setUnitId}
            title={title} setTitle={setTitle}
            titleFr={titleFr} setTitleFr={setTitleFr}
            description={description} setDescription={setDescription}
            availableUnits={availableUnits}
            sections={sections} setSections={setSections}
            mode={mode}
            addSection={addSection}
            removeSection={removeSection}
            renameSectionName={renameSectionName}
          />
        )}

        {step > 0 && !isReviewStep && currentSectionIndex >= 0 && currentSectionIndex < sections.length && (
          <StepSection
            section={sections[currentSectionIndex]}
            sectionIndex={currentSectionIndex}
            expandedBlockId={expandedBlockId}
            setExpandedBlockId={setExpandedBlockId}
            addBlockToSection={addBlockToSection}
            updateBlock={updateBlock}
            removeBlock={removeBlock}
            moveBlock={moveBlock}
            mode={mode}
            renameSectionName={renameSectionName}
          />
        )}

        {isReviewStep && (
          <StepReview
            title={title} titleFr={titleFr} description={description}
            unitId={unitId} units={units}
            sections={sections} allBlocks={allBlocks} totalMinutes={totalMinutes}
            saving={saving} saveError={saveError}
            onSave={handleSave} onExportSQL={exportSQL}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => { if (step === 0) setMode('select'); else setStep(s => s - 1) }}
          className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          {step === 0 ? 'Cambiar modo' : 'Anterior'}
        </button>
        {!isReviewStep && (
          <button
            onClick={() => setStep(s => s + 1)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================
// Step: Info General
// ============================================
function StepInfoGeneral({ unitId, setUnitId, title, setTitle, titleFr, setTitleFr, description, setDescription, availableUnits, sections, addSection, removeSection, mode, renameSectionName }: any) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900">Información general del taller</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="wizard-unit" className="block text-sm font-medium text-gray-600 mb-1">Unidad *</label>
          <select id="wizard-unit" value={unitId}
            onChange={e => setUnitId(e.target.value)}
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"
          >
            <option value="">Seleccionar unidad...</option>
            {availableUnits.map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.name_es || u.title || `Unidad ${u.unit_number}`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="wizard-title" className="block text-sm font-medium text-gray-600 mb-1">Título *</label>
          <input id="wizard-title" value={title} onChange={e => setTitle(e.target.value)}
            placeholder="Taller Unidad X: Tema del taller"
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"
          />
        </div>
        <div>
          <label htmlFor="wizard-title-fr" className="block text-sm font-medium text-gray-600 mb-1">Título en francés</label>
          <input
            id="wizard-title-fr"
            value={titleFr} onChange={e => setTitleFr(e.target.value)}
            placeholder="Atelier Unite X : ..."
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"
          />
        </div>
        <div>
          <label htmlFor="wizard-description" className="block text-sm font-medium text-gray-600 mb-1">Descripción</label>
          <input
            id="wizard-description"
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Breve descripción del taller"
            className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"
          />
        </div>
      </div>

      {/* Section management */}
      <div>
        <h3 className="text-sm font-bold text-gray-600 mb-3">
          Secciones del taller ({sections.length})
          {mode === 'structured' && <span className="text-gray-500 font-normal ml-2">Puedes renombrar las secciones temáticas</span>}
        </h3>
        <div className="space-y-2">
          {sections.map((s: SectionDraft, i: number) => (
            <div key={i} className="flex items-center gap-2 bg-gray-100/50 rounded-lg px-3 py-2">
              <span className="text-xs text-gray-500 font-mono w-5">{i + 1}</span>
              <input
                value={s.name}
                onChange={e => renameSectionName(i, e.target.value)}
                className="flex-1 bg-transparent border-none text-gray-900 text-sm outline-none"
              />
              <span className="text-xs text-gray-500">{s.blocks.length} bloques</span>
              {mode === 'free' && sections.length > 1 && (
                <button onClick={() => removeSection(i)} className="p-1 text-red-600 hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {mode === 'free' && (
          <button onClick={addSection} className="mt-2 text-sm text-purple-600 hover:text-purple-300 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Agregar sección
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================
// Step: Section editing
// ============================================
function StepSection({ section, sectionIndex, expandedBlockId, setExpandedBlockId, addBlockToSection, updateBlock, removeBlock, moveBlock }: any) {
  const [addingType, setAddingType] = useState('')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-900">{section.name}</h2>
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
            {section.blocks.length} bloques
          </span>
        </div>
      </div>

      {/* Blocks list */}
      <div className="space-y-2">
        {section.blocks.map((block: BlockDraft, bi: number) => {
          const isExpanded = expandedBlockId === block.id
          const typeLabel = BLOCK_TYPES.find(t => t.value === block.block_type)?.label || block.block_type

          return (
            <div key={block.id} className={`border rounded-xl transition-all ${isExpanded ? 'border-purple-500 bg-gray-50' : 'border-gray-200 bg-white/50'}`}>
              {/* Block header */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setExpandedBlockId(isExpanded ? null : block.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedBlockId(isExpanded ? null : block.id) }}
              >
                <div className="flex flex-col gap-0.5">
                  <button onClick={e => { e.stopPropagation(); moveBlock(sectionIndex, bi, -1) }} disabled={bi === 0}
                    className="text-gray-500 hover:text-gray-900 disabled:opacity-20 disabled:cursor-default"><ChevronLeft className="w-3 h-3 rotate-90" /></button>
                  <button onClick={e => { e.stopPropagation(); moveBlock(sectionIndex, bi, 1) }} disabled={bi === section.blocks.length - 1}
                    className="text-gray-500 hover:text-gray-900 disabled:opacity-20 disabled:cursor-default"><ChevronRight className="w-3 h-3 rotate-90" /></button>
                </div>
                <span className="text-xs font-mono text-gray-500 w-5">{bi + 1}</span>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{typeLabel}</span>
                <span className="text-sm text-gray-900 flex-1 truncate">{block.title || '(sin título)'}</span>
                <span className="text-xs text-gray-500">{block.estimated_minutes}min</span>
                <button onClick={e => { e.stopPropagation(); removeBlock(sectionIndex, bi) }} className="p-1 text-red-600 hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Block expanded form */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-3 pt-3">
                    <div>
                      <label htmlFor={`block-title-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Título del bloque</label>
                      <input id={`block-title-${block.id}`} value={block.title} onChange={e => updateBlock(sectionIndex, bi, { title: e.target.value })}
                        placeholder="Título..." className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
                    </div>
                    <div>
                      <label htmlFor={`block-minutes-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Minutos estimados</label>
                      <input id={`block-minutes-${block.id}`} type="number" value={block.estimated_minutes} onChange={e => updateBlock(sectionIndex, bi, { estimated_minutes: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
                    </div>
                    <div>
                      <label htmlFor={`block-type-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Tipo de bloque</label>
                      <select id={`block-type-${block.id}`} value={block.block_type}
                        onChange={e => updateBlock(sectionIndex, bi, { block_type: e.target.value, config: getDefaultConfig(e.target.value) })}
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm">
                        {BLOCK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`block-section-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Sección</label>
                      <input id={`block-section-${block.id}`} value={block.section} onChange={e => updateBlock(sectionIndex, bi, { section: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`block-instructions-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Instrucciones para el profesor</label>
                    <textarea id={`block-instructions-${block.id}`} value={block.instructions} onChange={e => updateBlock(sectionIndex, bi, { instructions: e.target.value })}
                      rows={2} placeholder="Que debe hacer el profesor con este bloque..."
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
                  </div>

                  <div>
                    <label htmlFor={`block-teacher-notes-${block.id}`}  className="block text-xs font-bold text-gray-500 mb-1">Notas del profesor (opcional)</label>
                    <textarea id={`block-teacher-notes-${block.id}`} value={block.teacher_notes} onChange={e => updateBlock(sectionIndex, bi, { teacher_notes: e.target.value })}
                      rows={2} placeholder="Tips, contexto, notas adicionales..."
                      className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
                  </div>

                  {/* Config form */}
                  <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-xs font-bold text-purple-600 mb-3 uppercase tracking-wide">Configuración del bloque</h4>
                    <BlockConfigForm
                      blockType={block.block_type}
                      config={block.config}
                      onChange={(config: any) => updateBlock(sectionIndex, bi, { config })}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Add block */}
      <div className="flex items-center gap-2">
        <select
          value={addingType}
          onChange={e => setAddingType(e.target.value)}
          className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
        >
          <option value="">Seleccionar tipo de bloque...</option>
          {BLOCK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button
          onClick={() => { if (addingType) { addBlockToSection(sectionIndex, addingType); setAddingType('') } }}
          disabled={!addingType}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
        >
          <Plus className="w-4 h-4" /> Agregar
        </button>
      </div>
    </div>
  )
}

// ============================================
// Step: Review
// ============================================
function StepReview({ title, titleFr, description, unitId, units, sections, allBlocks, totalMinutes, saving, saveError, onSave, onExportSQL }: any) {
  const unit = units.find((u: any) => u.id === unitId)
  const unitName = unit?.name_es || unit?.title || 'Sin unidad'

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
        <Eye className="w-5 h-5 text-green-700" /> Revisar y guardar
      </h2>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-100/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{sections.length}</p>
          <p className="text-xs text-gray-500">Secciones</p>
        </div>
        <div className="bg-gray-100/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{allBlocks.length}</p>
          <p className="text-xs text-gray-500">Bloques</p>
        </div>
        <div className="bg-gray-100/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">~{totalMinutes}</p>
          <p className="text-xs text-gray-500">Minutos</p>
        </div>
        <div className="bg-gray-100/50 rounded-lg p-3 text-center">
          <p className="text-sm font-bold text-purple-300 truncate">{unitName}</p>
          <p className="text-xs text-gray-500">Unidad</p>
        </div>
      </div>

      <div className="bg-gray-100/30 rounded-lg p-4 space-y-1">
        <p className="text-gray-900 font-medium">{title || '(sin título)'}</p>
        {titleFr && <p className="text-gray-500 text-sm">{titleFr}</p>}
        {description && <p className="text-gray-500 text-sm">{description}</p>}
      </div>

      {/* Sections overview */}
      <div className="space-y-3">
        {sections.map((s: SectionDraft, i: number) => (
          <div key={i} className="bg-gray-100/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-gray-600">{s.name}</h4>
              <span className="text-xs text-gray-500">{s.blocks.length} bloques | ~{s.blocks.reduce((sum: number, b: BlockDraft) => sum + b.estimated_minutes, 0)} min</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {s.blocks.map((b: BlockDraft, bi: number) => {
                const typeLabel = BLOCK_TYPES.find(t => t.value === b.block_type)?.label || b.block_type
                return (
                  <span key={bi} className="text-xs bg-gray-600/50 text-gray-600 px-2 py-1 rounded-md">
                    {b.title || typeLabel}
                  </span>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Validation warnings */}
      {(!unitId || !title.trim() || allBlocks.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-yellow-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <div>
            {!unitId && <p>Falta seleccionar una unidad</p>}
            {!title.trim() && <p>Falta el título del taller</p>}
            {allBlocks.length === 0 && <p>No hay bloques en el taller</p>}
          </div>
        </div>
      )}

      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
          {saveError}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving || !unitId || !title.trim() || allBlocks.length === 0}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Guardando...' : 'Guardar en base de datos'}
        </button>
        <button
          onClick={onExportSQL}
          disabled={!unitId || !title.trim() || allBlocks.length === 0}
          className="px-6 py-2.5 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
        >
          <Download className="w-4 h-4" /> Exportar SQL
        </button>
      </div>
    </div>
  )
}

// ============================================
// Helpers
// ============================================
function esc(s: string): string {
  return (s || '').replace(/'/g, "''")
}

function getDefaultConfig(blockType: string): Record<string, any> {
  const defaults: Record<string, any> = {
    title_slide: { heading: '', subheading: '', background_color: '#1e3a5f', show_date: false },
    vocabulary: { title: '', words: [{ fr: '', es: '', phonetic: '', example: '' }], show_phonetic: true, columns: 2 },
    phrase_list: { categories: [{ name: '', phrases: [{ fr: '', es: '', phonetic: '', context: '' }] }], highlight_key_words: true },
    dialogue: { title: '', characters: [{ name: '', color: '#3b82f6' }, { name: '', color: '#ef4444' }], lines: [{ character: '', fr: '', es: '' }], show_translation: true },
    roulette: { items: [''], colors: ['#ef4444', '#3b82f6', '#10b981', '#f59e0b'], title: '', subtitle: '', spin_duration_ms: 3500 },
    flashcards: { cards: [{ front: '', back: '', hint: '' }], shuffle: true, show_hint: true, layout: 'grid' },
    matching: { pairs: [{ left: '', right: '' }], left_label: 'Francais', right_label: 'Espanol', shuffle_sides: true },
    fill_blanks: { sentences: [{ text: '', answer: '', hint: '' }], show_hints: true, reveal_mode: 'one_by_one' },
    role_play: { scenario: '', scenario_en: '', roles: [{ name: '', description: '', suggested_phrases: [] }], duration_minutes: 3, tips: '' },
    image_describe: { image_url: '', caption: '', questions: [''], vocabulary_help: [''] },
    poll: { question: '', options: [{ text: '', is_correct: false }], show_correct: true, explanation: '' },
    timer_activity: { activity: '', activity_en: '', duration_seconds: 120, tips: [''], sound_on_finish: true },
    free_html: { html: '' },
    video_embed: { url: '', title: '', start_seconds: 0, end_seconds: 0, discussion_questions: [''] },
    audio_listen: { audio_url: '', title: '', transcript_en: '', transcript_es: '', show_transcript: false, questions: [''] },
    sentence_builder: { sentences: [{ words: [], correct_order: [], translation: '' }], show_translation: true },
    conversation_prompts: { prompts: [{ question_en: '', question_es: '', model_answer: '', difficulty: 'easy' }], mode: 'sequential', pairs_or_group: 'pairs', time_per_prompt_seconds: 60 },
  }
  return defaults[blockType] || {}
}
